import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { formatCurrency } from './radarUtils';

const QuedaGiroPage = () => {
  const [loading, setLoading] = useState(false);
  const [dias, setDias] = useState(15);
  const [estoqueStatus, setEstoqueStatus] = useState('todos');
  const [busca, setBusca] = useState('');
  const [secao, setSecao] = useState('todas');
  const [ordenacao, setOrdenacao] = useState('valor_desc');
  const [data, setData] = useState({ itens: [], valor_estoque_travado_total: 0 });

  const loadData = async (nextDias = dias, nextEstoque = estoqueStatus) => {
    try {
      setLoading(true);
      const response = await axios.get('/api/radar-lucro/queda-giro', {
        params: { dias: nextDias, estoque_status: nextEstoque },
      });
      setData(response.data || { itens: [], valor_estoque_travado_total: 0 });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar queda de giro');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [dias, estoqueStatus]);

  const secoes = useMemo(() => {
    const set = new Set((data.itens || []).map((i) => i.secao).filter(Boolean));
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
  }, [data.itens]);

  const itensFiltrados = useMemo(() => {
    const texto = busca.trim().toLowerCase();
    const base = (data.itens || []).filter((item) => {
      const matchBusca =
        !texto ||
        String(item.descricao || '').toLowerCase().includes(texto) ||
        String(item.codigoean || '').toLowerCase().includes(texto);
      const matchSecao = secao === 'todas' || (item.secao || '') === secao;
      return matchBusca && matchSecao;
    });
    const sorted = [...base];
    if (ordenacao === 'valor_desc') sorted.sort((a, b) => Number(b.valor_estoque_travado || 0) - Number(a.valor_estoque_travado || 0));
    if (ordenacao === 'valor_asc') sorted.sort((a, b) => Number(a.valor_estoque_travado || 0) - Number(b.valor_estoque_travado || 0));
    if (ordenacao === 'estoque_asc') sorted.sort((a, b) => Number(a.estoque_atual || 0) - Number(b.estoque_atual || 0));
    if (ordenacao === 'estoque_desc') sorted.sort((a, b) => Number(b.estoque_atual || 0) - Number(a.estoque_atual || 0));
    return sorted;
  }, [data.itens, busca, secao, ordenacao]);

  const valorFiltrado = useMemo(() => itensFiltrados.reduce((acc, i) => acc + Number(i.valor_estoque_travado || 0), 0), [itensFiltrados]);

  return (
    <div className="animate-fade-in space-y-4">
      <div className="rounded-xl border border-warning-200 bg-warning-50 p-4">
        <p className="text-sm font-semibold text-warning-800">Produtos que pararam de vender</p>
        <p className="text-2xl font-bold text-warning-900 mt-1">{formatCurrency(valorFiltrado)} em estoque travado no filtro atual</p>
        <p className="text-sm text-warning-800 mt-1">Voce pode estar acumulando capital em itens que perderam giro.</p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Queda de Giro</h1>
          <p className="text-gray-600">Venderam no periodo anterior, mas ficaram sem vendas no periodo atual</p>
        </div>
        <button className="btn-secondary" onClick={() => loadData()} disabled={loading}>
          Atualizar
        </button>
      </div>

      <div className="card">
        <div className="card-body grid grid-cols-1 md:grid-cols-5 gap-3">
          <select className="form-select" value={dias} onChange={(e) => setDias(Number(e.target.value))}>
            <option value={7}>Ultimos 7 dias</option>
            <option value={15}>Ultimos 15 dias</option>
            <option value={30}>Ultimos 30 dias</option>
            <option value={60}>Ultimos 60 dias</option>
          </select>
          <select className="form-select" value={estoqueStatus} onChange={(e) => setEstoqueStatus(e.target.value)}>
            <option value="todos">Todos os estoques</option>
            <option value="positivo">Estoque positivo</option>
            <option value="zerado">Estoque zerado</option>
            <option value="negativo">Estoque negativo</option>
          </select>
          <input className="form-input" placeholder="Buscar por descricao ou EAN" value={busca} onChange={(e) => setBusca(e.target.value)} />
          <select className="form-select" value={secao} onChange={(e) => setSecao(e.target.value)}>
            <option value="todas">Todas as secoes</option>
            {secoes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <select className="form-select" value={ordenacao} onChange={(e) => setOrdenacao(e.target.value)}>
            <option value="valor_desc">Maior impacto</option>
            <option value="valor_asc">Menor impacto</option>
            <option value="estoque_asc">Estoque asc</option>
            <option value="estoque_desc">Estoque desc</option>
          </select>
        </div>
        <div className="px-6 pb-4 text-sm text-gray-600">Itens filtrados: {itensFiltrados.length}</div>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>EAN</th>
                <th>Produto</th>
                <th>Secao</th>
                <th>Estoque</th>
                <th>Venda periodo anterior</th>
                <th>Venda periodo atual</th>
                <th>Estoque travado</th>
              </tr>
            </thead>
            <tbody>
              {itensFiltrados.map((item) => (
                <tr key={item.id_produto}>
                  <td>{item.codigoean || '-'}</td>
                  <td>{item.descricao}</td>
                  <td>{item.secao || '-'}</td>
                  <td>{item.estoque_atual}</td>
                  <td>{item.qtd_vendida_periodo_anterior}</td>
                  <td>{item.qtd_vendida_periodo_atual}</td>
                  <td>{formatCurrency(item.valor_estoque_travado)}</td>
                </tr>
              ))}
              {!loading && itensFiltrados.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-gray-500 py-8">
                    Nenhum item em queda de giro no filtro atual.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default QuedaGiroPage;
